import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAuth } from "../context/AuthContext";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import LandingPage from "../pages/Landing/LandingPage";
import RoleSelection from "../pages/Registration/RoleSelection";
import StudentRegistration from "../pages/Registration/StudentRegistration";
import ProfessionalRegistration from "../pages/Registration/ProfessionalRegistration";
import Login from "../pages/Login/Login";
import UniversityLogin from "../pages/Login/UniversityLogin";
import CompanyLogin from "../pages/Login/CompanyLogin";
import PlatformLogin from "../pages/Login/PlatformLogin";
import Home from "../pages/Home";
import TwoFactorSetup from "../pages/TwoFactorSetup";
import BoxesPage from "../pages/BoxesPage";
import LabPage from "../pages/LabPage";
import Profile from "../pages/Profile";
import Topology from "../pages/Topology";
import NotFound from "../pages/NotFound";
import RegisterAdmins from "../pages/Admin/RegisterAdmins";
import UserManagement from "../pages/Admin/UserManagement";
import BoxManagement from "../pages/Admin/BoxManagement";
import StudentManagement from "../pages/UniversityAdmin/StudentManagement";
import CompanyUserManagement from "../pages/CompanyAdmin/UserManagement";
import TeamsManagement from "../pages/CompanyAdmin/TeamsManagement";
import TeamChallenges from "../pages/CompanyAdmin/TeamChallenges";
import CompanyAdminApprovals from "../pages/CompanyAdmin/Approvals";
import RegisterOrganizationsPage from "../pages/Admin/RegisterOrganization";
import PlatformTeamChallenges from "../pages/Admin/PlatformTeamChallenges";
import PlatformTeamLabPage from "../pages/Admin/PlatformTeamLabPage";
import ProfilePage from "../pages/AdminsProfilePage";
import MyTeamPage from "../pages/CompanyUser/MyTeamPage";
import { hasAllPermissions } from "../utils/permissions";
import TeamLabPage from "../pages/CompanyUser/TeamLabPage";
import ThemePreview from "../pages/Prototype/ThemePreview";

function ProtectedRoute({
  children,
  blockedRoles = [],
  allowedRoles = [],
  requiredPermissions = [],
}) {
  const { user, isInitialized } = useAuth();

  if (!isInitialized) {
    return (
      <div className="app-bg-dark dark min-h-screen flex flex-col items-center justify-center">
        <div className="text-(--color-primary) text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const roles = user.roles || [];

  // If allowedRoles is specified, user must have at least one of those roles
  if (
    allowedRoles.length > 0 &&
    !roles.some((role) => allowedRoles.includes(role))
  ) {
    return <Navigate to="/home" replace />;
  }

  // If blockedRoles is specified, user must not have any of those roles
  if (
    blockedRoles.length > 0 &&
    roles.some((role) => blockedRoles.includes(role))
  ) {
    return <Navigate to="/home" replace />;
  }

  if (!hasAllPermissions(user, requiredPermissions)) {
    return <Navigate to="/home" replace />;
  }

  return children;
}

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [location.pathname]);

  return null;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <ScrollToTopOnRouteChange />
      <SentryRoutes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/register" element={<RoleSelection />} />
        <Route path="/register/student" element={<StudentRegistration />} />
        <Route
          path="/register/professional"
          element={<ProfessionalRegistration />}
        />
        <Route path="/login" element={<Login />} />
        <Route path="/university/login" element={<UniversityLogin />} />
        <Route path="/company/login" element={<CompanyLogin />} />
        <Route path="/platform/login" element={<PlatformLogin />} />
        <Route path="/2fa-setup" element={<TwoFactorSetup />} />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/boxes"
          element={
            <ProtectedRoute
              allowedRoles={[
                "student",
                "professional",
                "company_user",
                "company_admin",
                "platform_admin",
              ]}
              requiredPermissions={["catalog_view"]}
            >
              <BoxesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/boxes/:nodeId"
          element={
            <ProtectedRoute
              allowedRoles={[
                "student",
                "professional",
                "company_user",
                "company_admin",
                "platform_admin",
              ]}
              requiredPermissions={["catalog_view"]}
            >
              <BoxesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab/:labId"
          element={
            <ProtectedRoute
              allowedRoles={[
                "student",
                "professional",
                "company_user",
                "company_admin",
                "platform_admin",
              ]}
            >
              <LabPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-team"
          element={
            <ProtectedRoute allowedRoles={["company_user"]}>
              <MyTeamPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/team-lab/:labId"
          element={
            <ProtectedRoute allowedRoles={["company_user", "company_admin"]}>
              <TeamLabPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/topology"
          element={
            <ProtectedRoute
              allowedRoles={[
                "professional",
                "company_user",
                "company_admin",
                "platform_admin",
              ]}
              requiredPermissions={["topology_access"]}
            >
              <Topology />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/register-admins"
          element={
            <ProtectedRoute allowedRoles={["platform_admin"]}>
              <RegisterAdmins />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={["platform_admin"]}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/boxes"
          element={
            <ProtectedRoute allowedRoles={["platform_admin"]}>
              <BoxManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/profile"
          element={
            <ProtectedRoute
              allowedRoles={[
                "platform_admin",
                "university_admin",
                "company_admin",
              ]}
            >
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/register-organizations"
          element={
            <ProtectedRoute allowedRoles={["platform_admin"]}>
              <RegisterOrganizationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/team-challenges"
          element={
            <ProtectedRoute allowedRoles={["platform_admin"]}>
              <PlatformTeamChallenges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/team-lab/:labId"
          element={
            <ProtectedRoute allowedRoles={["platform_admin"]}>
              <PlatformTeamLabPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/university-admin/students"
          element={
            <ProtectedRoute allowedRoles={["university_admin"]}>
              <StudentManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company-admin/users"
          element={
            <ProtectedRoute allowedRoles={["company_admin"]}>
              <CompanyUserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company-admin/teams"
          element={
            <ProtectedRoute allowedRoles={["company_admin"]}>
              <TeamsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company-admin/team-challenges"
          element={
            <ProtectedRoute allowedRoles={["company_admin"]}>
              <TeamChallenges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company-admin/approvals"
          element={
            <ProtectedRoute allowedRoles={["company_admin"]}>
              <CompanyAdminApprovals />
            </ProtectedRoute>
          }
        />
        {/* PROTOTYPE — design exploration, delete with /pages/Prototype/ when picked */}
        <Route path="/prototype/theme-preview" element={<ThemePreview />} />
        <Route path="*" element={<NotFound />} />
      </SentryRoutes>
    </BrowserRouter>
  );
}
